import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ChevronRight, Droplets, Fuel, Users } from "lucide-react";
import type { FuelMonthSummary } from "@/lib/fuel-analytics";
import { fuelMonthPath } from "@/lib/fuel-analytics";
import { formatCurrency, formatFuelPrice, formatNumber } from "@/lib/utils";

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

type Props = {
  months: FuelMonthSummary[];
  basePath: string;
};

export function FuelMonthSummaryCards({ months, basePath }: Props) {
  if (months.length === 0) {
    return (
      <p className="rounded-2xl bg-canvas p-6 text-center text-sm text-muted">
        No fuel logs recorded yet.
      </p>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {months.map((entry, index) => (
          <motion.div
            key={`${entry.year}-${entry.month}`}
            variants={itemVariants}
            initial="hidden"
            animate="show"
            transition={{ delay: index * 0.05 }}
          >
            <Link
              to={fuelMonthPath(basePath, entry.year, entry.month)}
              className="group hover-raise block rounded-3xl bg-canvas p-5 hover:bg-surface"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-surface shadow-[var(--shadow-elevation)]">
                    <Calendar className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-extrabold tracking-tight">{entry.label}</p>
                    <p className="text-xs font-semibold text-muted">Monthly summary</p>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 -translate-x-1 text-muted opacity-0 transition-all duration-300 ease-[var(--ease-out-expo)] group-hover:translate-x-0 group-hover:opacity-100" />
              </div>

              <p className="mt-4 text-2xl font-extrabold tracking-tight">
                {formatCurrency(entry.overview.totalSpent)}
              </p>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-surface/80 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Fill-ups</p>
                  <p className="mt-1 flex items-center gap-1 text-sm font-bold">
                    <Fuel className="h-3.5 w-3.5 text-muted" />
                    {formatNumber(entry.overview.totalFillUps)}
                  </p>
                </div>
                <div className="rounded-2xl bg-surface/80 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Litres</p>
                  <p className="mt-1 flex items-center gap-1 text-sm font-bold">
                    <Droplets className="h-3.5 w-3.5 text-muted" />
                    {formatNumber(Math.round(entry.overview.totalLitres))} L
                  </p>
                </div>
                <div className="rounded-2xl bg-surface/80 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Active users</p>
                  <p className="mt-1 flex items-center gap-1 text-sm font-bold">
                    <Users className="h-3.5 w-3.5 text-muted" />
                    {formatNumber(entry.overview.activeUsers)}
                  </p>
                </div>
                <div className="rounded-2xl bg-surface/80 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Avg fill</p>
                  <p className="mt-1 text-sm font-bold">{formatCurrency(entry.overview.avgFillCost)}</p>
                  <p className="text-[10px] text-muted">{formatFuelPrice(entry.overview.avgPricePerLitre)}/L</p>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
    </div>
  );
}

export function FuelMonthTabs({
  months,
  year,
  activeMonth,
  basePath,
}: {
  months: number[];
  year: number;
  activeMonth: number;
  basePath: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {months.map((month) => {
        const active = month === activeMonth;
        const label = new Intl.DateTimeFormat("en-US", { month: "short" }).format(new Date(year, month, 1));
        return (
          <Link
            key={month}
            to={fuelMonthPath(basePath, year, month)}
            className={
              active
                ? "inline-flex items-center rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-surface"
                : "inline-flex items-center rounded-full bg-canvas px-4 py-2 text-sm font-semibold text-muted transition hover:bg-canvas-hover hover:text-foreground"
            }
          >
            {label}
          </Link>
        );
      })}
    </div>
  );
}
